import apiClient from './apiClient';

const api = apiClient;

export const createEmployee = async (employeeData) => {
  try {
    const response = await api.post('/employees', employeeData, { skipGlobalError: true });
    return {
      success: true,
      data: response.data.data || response.data,
      message: 'Employee created successfully'
    };
  } catch (error) {
    console.error('Error creating employee:', error);
    let errMsg = 'Failed to create employee';
    const responseData = error.response?.data;
    if (responseData) {
      if (responseData.error && Array.isArray(responseData.error)) {
        const valErrors = responseData.error.map(err => err.msg || err.message).join(', ');
        errMsg = `${responseData.message || 'Validation failed'}: ${valErrors}`;
      } else {
        errMsg = responseData.message || responseData.error || errMsg;
      }
    } else if (error.message) {
      errMsg = error.message;
    }
    return {
      success: false,
      message: errMsg,
      error: responseData?.error || error.message
    };
  }
};

export const bulkUploadEmployees = async (formData) => {
  try {
    const response = await api.post('/employees/bulk-upload', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  } catch (error) {
    console.error('Error bulk uploading employees:', error);
    throw error;
  }
};

export const uploadEmployeeFile = async (formData) => {
  try {
    const response = await api.post('/employees/upload', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  } catch (error) {
    console.error('Error uploading file:', error);
    throw error;
  }
};

export const getEmployeeById = async (id) => {
  try {
    const response = await api.get(`/employees/${id}`);
    return {
      success: true,
      data: response.data.data || response.data,
      message: 'Employee fetched successfully'
    };
  } catch (error) {
    console.error('Error fetching employee:', error);
    return {
      success: false,
      message: error.response?.data?.message || 'Failed to fetch employee',
      error: error.response?.data?.error || error.message
    };
  }
};

export const updateEmployee = async (id, updateData) => {
  try {
    const response = await api.put(`/employees/${id}`, updateData, { skipGlobalError: true });
    return {
      success: true,
      data: response.data.data || response.data,
      message: 'Employee updated successfully'
    };
  } catch (error) {
    console.error('Error updating employee:', error);
    return {
      success: false,
      message: error.response?.data?.message || 'Failed to update employee',
      error: error.response?.data?.error || error.message
    };
  }
};

export const getEmployees = async (filters = {}, config = {}) => {
  try {
    const response = await api.get('/employees', { 
      params: filters,
      ...config
    });
    return response.data;
  } catch (error) {
    console.error('Error fetching employees:', error);
    throw error;
  }
};

export const getEmployeesByEntity = async (entityId) => {
  try {
    const response = await api.get(`/employees/entity/${entityId}`);
    return response.data;
  } catch (error) {
    console.error('Error fetching employees by entity:', error);
    throw error;
  }
};

export const regenerateDocumentUploadToken = async (employeeId) => {
  try {
    const response = await api.post(`/employees/${employeeId}/regenerate-token`, {}, { skipGlobalError: true });
    return response.data;
  } catch (error) {
    console.error('Error regenerating document upload token:', error);
    throw error;
  }
};

export const getOnboardingDashboard = async (filters = {}) => {
  try {
    const response = await api.get('/employees/onboarding-dashboard', { params: filters });
    return response.data;
  } catch (error) {
    console.error('Error fetching onboarding dashboard:', error);
    throw error;
  }
};

export const updateBgvStatus = async (employeeId, data) => {
  try {
    const response = await api.put(`/employees/${employeeId}/bgv-status`, data, { skipGlobalError: true });
    return response.data;
  } catch (error) {
    console.error('Error updating BGV status:', error);
    throw error;
  }
};

export const updateOnboardingStatus = async (employeeId, status) => {
  try {
    const response = await api.put(`/employees/${employeeId}/onboarding-status`, { status }, { skipGlobalError: true });
    return response.data;
  } catch (error) {
    console.error('Error updating onboarding status:', error);
    throw error;
  }
};

export const inviteToDashboard = async (employeeId, data = {}) => {
  try {
    const response = await api.post(`/employees/${employeeId}/invite-dashboard`, data, { skipGlobalError: true });
    return response.data;
  } catch (error) {
    console.error('Error inviting to dashboard:', error);
    throw error;
  }
};

export const saveEmployeePersonalInfo = async (id, data) => {
  try {
    const response = await api.post(`/employees/${id}/personal-info`, data, {
      skipGlobalError: true,
    });
    return response.data;
  } catch (error) {
    console.error('Error saving personal info:', error);
    throw error;
  }
};

export const saveEmployeeEducation = async (id, data) => {
  try {
    const response = await api.post(`/employees/${id}/education`, data, {
      skipGlobalError: true,
    });
    return response.data;
  } catch (error) {
    console.error('Error saving education info:', error);
    throw error;
  }
};

export const saveEmployeeExperience = async (id, data) => {
  try {
    const response = await api.post(`/employees/${id}/experience`, data, {
      skipGlobalError: true,
    });
    return response.data;
  } catch (error) {
    console.error('Error saving experience info:', error);
    throw error;
  }
};

export const saveEmployeeBankDetails = async (id, data) => {
  try {
    const response = await api.post(`/employees/${id}/bank-details`, data, { 
      skipGlobalError: true 
    });
    return response.data;
  } catch (error) {
    console.error('Error saving bank details:', error);
    throw error;
  }
};

export const saveEmployeeCompensation = async (id, data) => {
  try {
    const response = await api.post(`/employees/${id}/compensation-info`, data, {
      skipGlobalError: true,
    });
    return response.data;
  } catch (error) {
    console.error('Error saving compensation info:', error);
    throw error;
  }
};

export const saveEmployeeJoiningForms = async (id, data) => {
  try {
    const response = await api.post(`/employees/${id}/joining-forms`, data, {
      skipGlobalError: true,
    });
    return response.data;
  } catch (error) {
    console.error('Error saving joining forms:', error);
    throw error;
  }
};

export const updateDocumentStatus = async (documentId, status) => {
  try {
    const response = await api.put(`/onboarding/documents/${documentId}/status`, { status }, { skipGlobalError: true });
    return response.data;
  } catch (error) {
    console.error('Error updating document status:', error);
    throw error;
  }
};
export const updateBankDetailsStatus = async (employeeId, status) => {
  try {
    const response = await api.put(`/onboarding/bank-details/${employeeId}/status`, { status }, { skipGlobalError: true });
    return response.data;
  } catch (error) {
    console.error('Error updating bank details status:', error);
    throw error;
  }
};

export const updateIdentityStatus = async (employeeId, documentType, status) => {
  try {
    const response = await api.put(`/onboarding/identities/${employeeId}/verify`, { documentType, status }, { skipGlobalError: true });
    return response.data;
  } catch (error) {
    console.error('Error updating identity status:', error);
    throw error;
  }
};
export const exportEmployeesToExcel = async (filters = {}) => {
  try {
    const response = await api.get('/employees/export-excel', {
      params: filters,
      responseType: 'blob',
    });
    return response.data;
  } catch (error) {
    console.error('Error exporting employees to excel:', error);
    throw error;
  }
};

export const getEmployeesPendingDocuments = async (filters = {}) => {
  try {
    const response = await api.get('/employees/pending-documents', { params: filters });
    return response.data;
  } catch (error) {
    console.error('Error fetching employees pending documents:', error);
    throw error;
  }
};
